/**
 * Game State Store
 * Zustand store for managing game state and actions
 */

import { create } from 'zustand';
import type { GameState, Card } from '../types/game';
import { createInitialGameState, GAME_CONFIG } from '../types/game';
import { buildDeck, shuffle, dealCards, determineTrump, sortHand } from '../engine/deck';
import { checkGameOver, collectPlayZoneCards, canAttack, cardBeats } from '../engine/durak';
import { botSelectAttack, botDefend, BOT_CONFIGS } from '../engine/bot';
import type { Question, AnsweredQuestion, Perspective } from '../types/question';
import { getRandomQuestion, getQuestionById, QUESTIONS } from '../data/questions';
import { PERSPECTIVES } from '../data/perspectives';
import { getCardDifficulty, getXpReward, getTimeLimit } from '../types/game';
import { playCardFlip, playCorrect, playWrong, playWin, playLose, playHint } from '../utils/sound';
import { saveGameProgress, clearGameProgress } from './persist';

/** Stable key for indexing a card in the question-binding map */
function cardKey(card: Card): string {
  return `${card.suit}-${card.rank}`;
}

/**
 * Build a 1:1 binding between the 36-card deck and 36 unique questions.
 *
 * For every card in the deck we pick an as-yet-unused question whose
 * difficulty matches the card's difficulty tier. This guarantees that
 * within a single game no two cards ever surface the same question,
 * which was the root of the "similar cards keep appearing" feedback:
 * previously the same Q could be drawn many times because selection
 * was a per-difficulty random pick without game-scoped uniqueness.
 */
function buildCardQuestionMap(deck: readonly Card[]): Record<string, string> {
  const map: Record<string, string> = {};
  const usedIds = new Set<string>();

  // Pre-bucket questions by difficulty so we can pop without re-filtering.
  const buckets: Record<'easy' | 'medium' | 'hard', Question[]> = {
    easy: shuffle(QUESTIONS.filter(q => q.difficulty === 'easy')),
    medium: shuffle(QUESTIONS.filter(q => q.difficulty === 'medium')),
    hard: shuffle(QUESTIONS.filter(q => q.difficulty === 'hard')),
  };

  for (const card of deck) {
    const diff = getCardDifficulty(card.rank);
    const bucket = buckets[diff];

    // Walk the shuffled bucket and take the first unused id.
    let pickIdx = bucket.findIndex(q => !usedIds.has(q.id));
    if (pickIdx === -1) {
      // Pool exhausted at this difficulty — allow re-use (rare, but safe
      // if the corpus shrinks below 36 of a single difficulty).
      pickIdx = 0;
    }
    const picked = bucket[pickIdx];
    usedIds.add(picked.id);
    map[cardKey(card)] = picked.id;
  }

  return map;
}

export type GameScreen = 'landing' | 'game' | 'question' | 'perspective' | 'review' | 'tracker';

interface GameStore extends GameState {
  // Navigation
  currentScreen: GameScreen;
  setScreen: (screen: GameScreen) => void;

  // Game flow
  startGame: () => void;
  startDemo: () => void;
  selectCard: (card: Card) => void;
  playPendingCard: () => void;
  pass: () => void;
  take: () => void;

  // Question handling
  currentQuestion: Question | null;
  questionTimeRemaining: number;
  selectedOption: number | null;
  hasAnswered: boolean;
  answerCorrect: boolean | null;
  xpGain: number;
  pendingPerspective: Perspective | null;
  /** Indices of options eliminated by the hint (50/50) */
  eliminatedOptions: number[];
  hintUsed: boolean;
  /** Perspective indices seen this game — ensures non-repeating sequential order */
  seenPerspectiveIndices: number[];
  /** True briefly when the question pool cycles back to the beginning */
  poolResetToast: boolean;
  clearPoolResetToast: () => void;
  /** Map from "suit-rank" → question id; built once per game so each of the
   *  36 cards is bound to a unique question for the entire match. */
  cardQuestionMap: Record<string, string>;

  startQuestion: (card: Card) => void;
  selectOption: (index: number) => void;
  submitAnswer: () => void;
  /** Auto-submit on timer expiry — treated as wrong answer */
  timeoutAnswer: () => void;
  continueAfterQuestion: () => void;
  decrementTimer: () => void;
  extendTimer: () => void;
  useHint: () => void;

  // Perspective handling
  completePerspective: (answer: 0 | 1 | 2) => void;
  skipPerspective: () => void;

  // Bot actions
  botDifficulty: 'easy' | 'medium' | 'hard';
  setBotDifficulty: (difficulty: 'easy' | 'medium' | 'hard') => void;
  executeBotTurn: () => void;

  // Match tracking
  matchStartTime: number | null;
  getMatchDuration: () => string;

  // Internal game flow methods
  endBout: (discardCards: boolean) => void;
  drawCards: () => void;

  // Reset
  resetGame: () => void;
}

export const useGameStore = create<GameStore>((set, get) => ({
  ...createInitialGameState(),
  currentScreen: 'landing',

  // Question state
  currentQuestion: null,
  questionTimeRemaining: 0,
  selectedOption: null,
  hasAnswered: false,
  answerCorrect: null,
  xpGain: 0,
  pendingPerspective: null,
  eliminatedOptions: [],
  hintUsed: false,
  seenPerspectiveIndices: [],
  poolResetToast: false,
  cardQuestionMap: {},

  // Bot config
  botDifficulty: 'medium',

  // Navigation
  setScreen: (screen) => set({ currentScreen: screen }),

  // Start new game
  startGame: () => {
    const fullDeck = shuffle(buildDeck());
    const { trump, trumpCard } = determineTrump(fullDeck);

    // Build the per-game card→question binding BEFORE dealing — covers
    // all 36 cards in the freshly built deck, so any card a player ever
    // draws (including from the bottom trump card) already has a question.
    const cardQuestionMap = buildCardQuestionMap(fullDeck);

    // Deal 6 cards each
    const playerDealt = dealCards(fullDeck, GAME_CONFIG.INITIAL_HAND_SIZE);
    const playerHand = sortHand(playerDealt.dealt, trump);

    const botDealt = dealCards(playerDealt.remaining, GAME_CONFIG.INITIAL_HAND_SIZE);
    const botHand = botDealt.dealt;

    const remainingDeck = botDealt.remaining;

    set({
      ...createInitialGameState(),
      deck: remainingDeck,
      trump,
      trumpCard,
      playerHand,
      botHand,
      matchStartTime: Date.now(),
      currentScreen: 'game',
      currentQuestion: null,
      questionTimeRemaining: 0,
      selectedOption: null,
      hasAnswered: false,
      answerCorrect: null,
      xpGain: 0,
      pendingPerspective: null,
      eliminatedOptions: [],
      hintUsed: false,
      seenPerspectiveIndices: [],
      poolResetToast: false,
      cardQuestionMap,
    });
  },

  // Start demo game — Easy bot, guaranteed trump card in hand for the Perspective demo
  startDemo: () => {
    const fullDeck = shuffle(buildDeck());
    const { trump, trumpCard } = determineTrump(fullDeck);
    const cardQuestionMap = buildCardQuestionMap(fullDeck);

    const playerDealt = dealCards(fullDeck, GAME_CONFIG.INITIAL_HAND_SIZE);
    let playerHand = sortHand(playerDealt.dealt, trump);

    const botDealt = dealCards(playerDealt.remaining, GAME_CONFIG.INITIAL_HAND_SIZE);
    const botHand = botDealt.dealt;
    let remainingDeck = botDealt.remaining;

    // Ensure player has at least one trump-suit card so the jury can see a Perspective Challenge
    const hasTrump = playerHand.some(c => c.suit === trump);
    if (!hasTrump) {
      const deckTrumpIdx = remainingDeck.findIndex(c => c.suit === trump);
      const nonTrumpIdx = playerHand.findIndex(c => c.suit !== trump);
      if (deckTrumpIdx !== -1 && nonTrumpIdx !== -1) {
        const trumpCardFromDeck = remainingDeck[deckTrumpIdx];
        const displaced = playerHand[nonTrumpIdx];
        remainingDeck = [
          ...remainingDeck.slice(0, deckTrumpIdx),
          displaced,
          ...remainingDeck.slice(deckTrumpIdx + 1),
        ];
        playerHand = sortHand(
          playerHand.map((c, i) => (i === nonTrumpIdx ? trumpCardFromDeck : c)),
          trump
        );
      }
    }

    set({
      ...createInitialGameState(),
      deck: remainingDeck,
      trump,
      trumpCard,
      playerHand,
      botHand,
      botDifficulty: 'easy',
      matchStartTime: Date.now(),
      currentScreen: 'game',
      currentQuestion: null,
      questionTimeRemaining: 0,
      selectedOption: null,
      hasAnswered: false,
      answerCorrect: null,
      xpGain: 0,
      pendingPerspective: null,
      eliminatedOptions: [],
      hintUsed: false,
      seenPerspectiveIndices: [],
      poolResetToast: false,
      cardQuestionMap,
    });
  },

  // Select a card to play
  selectCard: (card) => {
    const state = get();

    // Validate card can be played
    if (state.isPlayerAttacking) {
      // Check if can attack
      if (!canAttack(state.playZone, card)) {
        return; // Invalid attack
      }
    } else {
      // Check if can defend
      const openSlot = state.playZone.find(s => !s.defender);
      if (!openSlot) return;
      if (!cardBeats(openSlot.attacker, card, state.trump)) {
        return; // Cannot defend
      }
    }

    set({
      pendingCard: card,
      currentScreen: 'question',
    });

    // Start question for this card
    get().startQuestion(card);
  },

  // Start question for a card
  startQuestion: (card) => {
    const difficulty = getCardDifficulty(card.rank);
    const timeLimit = getTimeLimit(difficulty);
    const state = get();
    const askedIds = state.answered.map(a => a.id);

    // Prefer the question pre-bound to this card for the current game so
    // that the same card always surfaces the same question (and no two of
    // the 36 cards ever share a question within one match).
    let question: Question | null = null;
    const boundId = state.cardQuestionMap[cardKey(card)];
    if (boundId) {
      question = getQuestionById(boundId) ?? null;
    }

    // Fallback for legacy state without a binding (e.g. restored saves)
    let poolReset = false;
    if (!question) {
      const available = QUESTIONS.filter(
        q => q.difficulty === difficulty && !askedIds.includes(q.id)
      );
      poolReset = available.length === 0;
      question = getRandomQuestion(difficulty, poolReset ? [] : askedIds);
    }

    // Sequential non-repeating perspective selection for trump cards
    const isTrump = card.suit === state.trump;
    let perspective: Perspective | null = null;
    let newSeenPerspectiveIndices = state.seenPerspectiveIndices;

    if (isTrump) {
      const allIndices = PERSPECTIVES.map((_, i) => i);
      const unseen = allIndices.filter(i => !newSeenPerspectiveIndices.includes(i));

      let nextIndex: number;
      if (unseen.length === 0) {
        // Full cycle complete — restart from index 0
        nextIndex = 0;
        newSeenPerspectiveIndices = [0];
      } else {
        nextIndex = unseen[0];
        newSeenPerspectiveIndices = [...newSeenPerspectiveIndices, nextIndex];
      }
      perspective = PERSPECTIVES[nextIndex];
    }

    set({
      currentQuestion: question,
      questionTimeRemaining: timeLimit,
      selectedOption: null,
      hasAnswered: false,
      answerCorrect: null,
      xpGain: 0,
      pendingPerspective: perspective,
      eliminatedOptions: [],
      hintUsed: false,
      poolResetToast: poolReset,
      seenPerspectiveIndices: newSeenPerspectiveIndices,
    });
  },

  // Select an answer option
  selectOption: (index) => set({ selectedOption: index }),

  // Dismiss the pool-reset toast
  clearPoolResetToast: () => set({ poolResetToast: false }),

  // Submit answer
  submitAnswer: () => {
    const state = get();
    if (state.selectedOption === null || !state.currentQuestion) return;

    const isCorrect = state.selectedOption === state.currentQuestion.correct;
    const difficulty = state.currentQuestion.difficulty;

    // Engagement: streak combo bonus. After 3 correct in a row, every
    // subsequent correct answer earns +25 % XP (capped at +100 %). This
    // keeps the player motivated to chain answers instead of treating
    // each card as an independent draw.
    const baseXp = isCorrect ? getXpReward(difficulty) : 0;
    const newStreak = isCorrect ? state.streak + 1 : 0;
    const comboMultiplier = isCorrect
      ? Math.min(1 + Math.max(0, newStreak - 3) * 0.25, 2)
      : 1;
    const xpGain = Math.round(baseXp * comboMultiplier);

    // Play sound effect
    if (isCorrect) {
      playCorrect();
    } else {
      playWrong();
    }

    // Record answered question
    const answered: AnsweredQuestion = {
      ...state.currentQuestion,
      wasCorrect: isCorrect,
      xpGain,
      answeredAt: Date.now(),
    };

    set({
      hasAnswered: true,
      answerCorrect: isCorrect,
      xpGain,
      xp: state.xp + xpGain,
      correctCount: isCorrect ? state.correctCount + 1 : state.correctCount,
      totalQuestions: state.totalQuestions + 1,
      streak: newStreak,
      bestStreak: isCorrect
        ? Math.max(state.bestStreak, newStreak)
        : state.bestStreak,
      answered: [...state.answered, answered],
    });
  },

  // Timeout answer (auto-submit when timer expires)
  timeoutAnswer: () => {
    const state = get();
    if (!state.currentQuestion || state.hasAnswered) return;

    playWrong();

    // Record as wrong answer with 0 XP
    const answered: AnsweredQuestion = {
      ...state.currentQuestion,
      wasCorrect: false,
      xpGain: 0,
      answeredAt: Date.now(),
    };

    set({
      hasAnswered: true,
      answerCorrect: false,
      xpGain: 0,
      totalQuestions: state.totalQuestions + 1,
      streak: 0,
      answered: [...state.answered, answered],
    });
  },

  // Continue after question
  continueAfterQuestion: () => {
    const state = get();

    if (!state.answerCorrect) {
      // Wrong answer - player takes the attacking card(s) as penalty
      // This matches real Durak rules: failing to defend = taking cards
      const attackingCards = state.playZone.map(slot => slot.attacker);
      const newHand = sortHand([...state.playerHand, ...attackingCards], state.trump);

      set({
        playerHand: newHand,
        playZone: [], // Clear play zone
        pendingCard: null,
        currentScreen: 'game',
        currentQuestion: null,
        streak: 0,
      });

      // Draw cards to refill hands
      get().drawCards();

      // Check game over after taking cards
      const gameStatus = checkGameOver(
        get().deck.length,
        get().playerHand.length,
        get().botHand.length
      );

      if (gameStatus.ended) {
        set({ currentScreen: 'review' });
        return;
      }

      // Bot attacks again
      set({ isPlayerAttacking: false });
      setTimeout(() => get().executeBotTurn(), 800);
      return;
    }

    // Correct answer - check for perspective challenge
    if (state.pendingPerspective) {
      set({ currentScreen: 'perspective' });
      return;
    }

    // Play the card
    get().playPendingCard();
  },

  // Play the pending card
  playPendingCard: () => {
    const state = get();
    const card = state.pendingCard;
    if (!card) return;

    playCardFlip();

    if (state.isPlayerAttacking) {
      // Add to play zone
      set({
        playZone: [...state.playZone, { attacker: card, defender: null }],
        playerHand: state.playerHand.filter(
          c => !(c.suit === card.suit && c.rank === card.rank)
        ),
        pendingCard: null,
        currentScreen: 'game',
        currentQuestion: null,
        pendingPerspective: null,
      });

      // Bot defends
      setTimeout(() => get().executeBotTurn(), 800);
    } else {
      // Defend
      const openSlotIndex = state.playZone.findIndex(s => !s.defender);
      if (openSlotIndex === -1) return;

      const newPlayZone = [...state.playZone];
      newPlayZone[openSlotIndex] = {
        ...newPlayZone[openSlotIndex],
        defender: card,
      };

      set({
        playZone: newPlayZone,
        playerHand: state.playerHand.filter(
          c => !(c.suit === card.suit && c.rank === card.rank)
        ),
        pendingCard: null,
        currentScreen: 'game',
        currentQuestion: null,
        pendingPerspective: null,
      });

      // Check if all defended
      const allDefended = newPlayZone.every(s => s.defender);
      if (allDefended) {
        setTimeout(() => {
          get().endBout(true);
        }, 800);
      }
    }
  },

  // Decrement timer
  decrementTimer: () => {
    const state = get();
    if (state.questionTimeRemaining > 0) {
      const newTime = state.questionTimeRemaining - 1;
      set({ questionTimeRemaining: newTime });
      
      // Auto-submit when timer hits zero
      if (newTime === 0 && !state.hasAnswered) {
        get().timeoutAnswer();
      }
    }
  },

  // Extend timer (costs XP)
  extendTimer: () => {
    const state = get();
    if (state.xp >= GAME_CONFIG.EXTEND_COST) {
      set({
        xp: state.xp - GAME_CONFIG.EXTEND_COST,
        questionTimeRemaining: state.questionTimeRemaining + GAME_CONFIG.EXTEND_SECONDS,
      });
    }
  },

  // Use hint (50/50 - eliminates 2 wrong answers, costs XP)
  useHint: () => {
    const state = get();
    if (!state.currentQuestion || state.hasAnswered || state.hintUsed) return;
    if (state.xp < GAME_CONFIG.HINT_COST) return;

    const correctIndex = state.currentQuestion.correct;
    const allIndices = [0, 1, 2, 3];
    
    // Get wrong answer indices
    const wrongIndices = allIndices.filter(i => i !== correctIndex);
    
    // Randomly select 2 wrong answers to eliminate
    const shuffled = [...wrongIndices].sort(() => Math.random() - 0.5);
    const eliminated = shuffled.slice(0, 2);

    playHint();

    set({
      xp: state.xp - GAME_CONFIG.HINT_COST,
      eliminatedOptions: eliminated,
      hintUsed: true,
    });
  },

  // Complete perspective challenge
  completePerspective: (answer) => {
    const state = get();

    // Award bonus XP for correct perspective answer
    if (state.pendingPerspective && answer === state.pendingPerspective.correctAnswer) {
      set({ xp: state.xp + GAME_CONFIG.PERSPECTIVE_BONUS });
    }

    get().playPendingCard();
  },

  // Skip perspective challenge
  skipPerspective: () => {
    get().playPendingCard();
  },

  // Pass turn
  pass: () => {
    const state = get();
    if (state.playZone.length === 0) return;

    get().endBout(true);
  },

  // Take cards (defender gives up)
  take: () => {
    const state = get();

    // Add all play zone cards to player hand
    const takenCards = collectPlayZoneCards(state.playZone);

    set({
      playerHand: sortHand([...state.playerHand, ...takenCards], state.trump),
      playZone: [],
      streak: 0,
    });

    // Draw cards
    get().drawCards();

    // Bot attacks again
    set({
      isPlayerAttacking: false,
      gamePhase: 'attack',
    });

    setTimeout(() => get().executeBotTurn(), 1000);
  },

  // End bout (round)
  endBout: (discardCards: boolean) => {
    const state = get();

    if (discardCards) {
      // Move play zone to discard
      const cards = collectPlayZoneCards(state.playZone);
      set({
        discard: [...state.discard, ...cards],
        playZone: [],
      });
    }

    // Draw cards
    get().drawCards();

    // Check game over
    const gameStatus = checkGameOver(
      get().deck.length,
      get().playerHand.length,
      get().botHand.length
    );

    if (gameStatus.ended) {
      // Play win/lose sound
      if (gameStatus.playerWon) {
        playWin();
      } else {
        playLose();
      }
      
      // Clear saved progress on game end
      clearGameProgress();
      
      set({
        currentScreen: 'review',
        gamePhase: 'ended',
      });
      return;
    }

    // Save progress after bout
    const currentState = get();
    saveGameProgress({
      deck: currentState.deck,
      trump: currentState.trump,
      trumpCard: currentState.trumpCard,
      discard: currentState.discard,
      playerHand: currentState.playerHand,
      botHand: currentState.botHand,
      playZone: currentState.playZone,
      isPlayerAttacking: currentState.isPlayerAttacking,
      gamePhase: currentState.gamePhase,
      answered: currentState.answered,
      matchStartTime: currentState.matchStartTime,
    });

    // Swap attacker
    set({
      isPlayerAttacking: !state.isPlayerAttacking,
      gamePhase: 'attack',
    });

    // If bot is now attacking, execute bot turn
    if (!get().isPlayerAttacking) {
      setTimeout(() => get().executeBotTurn(), 1000);
    }
  },

  // Draw cards to refill hands
  drawCards: () => {
    const state = get();
    const newState = { ...state };

    // Attacker draws first
    const attackerHand = state.isPlayerAttacking ? newState.playerHand : newState.botHand;
    const defenderHand = state.isPlayerAttacking ? newState.botHand : newState.playerHand;

    // Attacker draws to 6
    while (attackerHand.length < GAME_CONFIG.INITIAL_HAND_SIZE && newState.deck.length > 0) {
      attackerHand.push(newState.deck.shift()!);
    }

    // Defender draws to 6
    while (defenderHand.length < GAME_CONFIG.INITIAL_HAND_SIZE && newState.deck.length > 0) {
      defenderHand.push(newState.deck.shift()!);
    }

    // Sort player hand
    if (state.isPlayerAttacking) {
      newState.playerHand = sortHand(newState.playerHand, newState.trump);
    } else {
      newState.playerHand = sortHand(newState.playerHand, newState.trump);
    }

    set(newState);
  },

  // Execute bot turn
  executeBotTurn: () => {
    const state = get();
    const config = BOT_CONFIGS[state.botDifficulty];

    if (state.isPlayerAttacking) {
      // Bot is defending
      const defense = botDefend(state.botHand, state.playZone, state.trump, config);

      if (defense.defended) {
        // Apply defenses
        const newPlayZone = [...state.playZone];
        const newBotHand = [...state.botHand];

        for (const d of defense.defenses) {
          newPlayZone[d.slotIndex] = {
            ...newPlayZone[d.slotIndex],
            defender: d.card,
          };

          // Remove card from bot hand
          const idx = newBotHand.findIndex(
            c => c.suit === d.card.suit && c.rank === d.card.rank
          );
          if (idx > -1) newBotHand.splice(idx, 1);
        }

        set({
          playZone: newPlayZone,
          botHand: newBotHand,
        });

        // Check if all defended
        if (newPlayZone.every(s => s.defender)) {
          setTimeout(() => get().endBout(true), 800);
        }
      } else {
        // Bot takes cards
        const takenCards = collectPlayZoneCards(state.playZone);
        set({
          botHand: [...state.botHand, ...takenCards],
          playZone: [],
        });

        get().drawCards();

        // Check game over
        const gameStatus = checkGameOver(
          get().deck.length,
          get().playerHand.length,
          get().botHand.length
        );

        if (gameStatus.ended) {
          set({ currentScreen: 'review' });
          return;
        }

        // Player attacks again
        set({ isPlayerAttacking: true });
      }
    } else {
      // Bot is attacking
      const attack = botSelectAttack(state.botHand, state.playZone, state.trump, config);

      if (attack.action === 'attack' && attack.card) {
        set({
          playZone: [...state.playZone, { attacker: attack.card, defender: null }],
          botHand: state.botHand.filter(
            c => !(c.suit === attack.card!.suit && c.rank === attack.card!.rank)
          ),
        });
      } else {
        // Bot passes - end bout
        get().endBout(true);
      }
    }
  },

  // Set bot difficulty
  setBotDifficulty: (difficulty) => set({ botDifficulty: difficulty }),

  // Get match duration
  getMatchDuration: () => {
    const state = get();
    if (!state.matchStartTime) return '0:00';

    const elapsed = Math.floor((Date.now() - state.matchStartTime) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  },

  // Reset game
  resetGame: () => {
    set({
      ...createInitialGameState(),
      currentScreen: 'landing',
      currentQuestion: null,
      questionTimeRemaining: 0,
      selectedOption: null,
      hasAnswered: false,
      answerCorrect: null,
      xpGain: 0,
      pendingPerspective: null,
      eliminatedOptions: [],
      hintUsed: false,
      seenPerspectiveIndices: [],
      poolResetToast: false,
      cardQuestionMap: {},
    });
  },
}));
